import {
  createContext,
  PropsWithChildren,
  useCallback,
  use,
  useMemo,
} from "react";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { doc, orderBy, query, updateDoc } from "firebase/firestore";
import {
  getOrganisationsCollection,
  Organisation,
} from "~/integrations/firebase/collections";
import { useApi } from "~/hooks/use-api";

const collection = getOrganisationsCollection();

interface IOrganisationsContext {
  organisations: Organisation[];
  loading: boolean;

  addOrganisation(organisation: Pick<Organisation, "name">): Promise<void>;
  updateOrganisationName(id: string, name: string): Promise<void>;
}

export const OrganisationsContext = createContext<IOrganisationsContext>(
  undefined as never,
);

export const OrganisationsProvider = ({ children }: PropsWithChildren) => {
  const api = useApi();
  const [allOrganisations = [], loading] = useCollectionData(
    query(collection, orderBy("name")),
  );

  const organisations = useMemo(() => {
    return allOrganisations.filter(
      (organisation) => !organisation.hardDeletedAt,
    );
  }, [allOrganisations]);

  const addOrganisation = useCallback(
    async (organisation: Pick<Organisation, "name">) => {
      await api.account.organisations.createOrganisation(organisation);
    },
    [api],
  );

  const updateOrganisationName = useCallback(
    (id: string, name: Organisation["name"]) =>
      updateDoc(doc(getOrganisationsCollection(), id), "name", name),
    [],
  );

  const value = useMemo(
    () => ({
      organisations,
      addOrganisation,
      loading,
      updateOrganisationName,
    }),
    [organisations, addOrganisation, loading, updateOrganisationName],
  );

  return <OrganisationsContext value={value}>{children}</OrganisationsContext>;
};

export const useOrganisations = () => {
  const context = use(OrganisationsContext);

  if (!context) {
    throw new Error(
      "useOrganisations must be used within a OrganisationsProvider",
    );
  }

  return context;
};
